import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './SignaturePad.scss';

export interface SignaturePadHandle {
  /** PNG data URL of what's drawn, or null if the pad is empty. */
  getDataUrl: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

interface Props {
  className?: string;
}

/**
 * A minimal canvas-based signature pad — no dependency added for this, since the codebase has no
 * existing drawing surface to build on and the interaction (track a pointer, stroke a line) is
 * small enough not to warrant one. Pointer Events (not separate mouse/touch handlers) so mouse,
 * touch and pen all work through one code path.
 */
const SignaturePad = forwardRef<SignaturePadHandle, Props>(({ className }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasStrokeRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Backs the canvas with real device pixels so strokes stay crisp on high-DPI screens, while the
  // CSS size (set in SignaturePad.scss) stays a normal, layout-friendly size.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.25;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#18181B';
    }
  }, []);

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(e);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const from = lastPointRef.current;
    if (!ctx || !from) return;
    const to = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    lastPointRef.current = to;
    if (!hasStrokeRef.current) {
      hasStrokeRef.current = true;
      setIsEmpty(false);
    }
  };

  const endStroke = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  useImperativeHandle(ref, () => ({
    getDataUrl: () => (hasStrokeRef.current ? canvasRef.current?.toDataURL('image/png') ?? null : null),
    clear: () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      hasStrokeRef.current = false;
      setIsEmpty(true);
    },
    isEmpty: () => !hasStrokeRef.current,
  }));

  return (
    <div className={`signature-pad ${className ?? ''}`}>
      <canvas
        ref={canvasRef}
        className="signature-pad-canvas"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endStroke}
        onPointerLeave={endStroke}
      />
      {isEmpty && <span className="signature-pad-placeholder">Draw your signature here</span>}
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
